import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Heart, MessageCircle, Eye, Send, ArrowLeft, ArrowRight } from 'lucide-react';
import '../styles/BookReader.css';
import getProfileImageUrl from '../utils/getProfileImageUrl';

const BookReader = () => {
  const { bookId, chapterId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showChapters, setShowChapters] = useState(false);
  const [likedChapters, setLikedChapters] = useState({});
  const [views, setViews] = useState(0);
  const [loading, setLoading] = useState(true);
  const currentUserId = parseInt(localStorage.getItem('userId'), 10);

  const fetchBookDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await api.get(`/bookReader/books/${bookId}/${chapterId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data;
      const authorName = data.chapters[0]?.author_name || 'Bilinmeyen Yazar';
      const publishedChapters = data.chapters.filter(chap => chap.is_published);
      const chapter = publishedChapters.find(chap => chap.chapter_id === parseInt(chapterId, 10));
      const initialViews = chapter?.views || 0;
      const updatedLikedChapters = {};
      publishedChapters.forEach(chap => {
        updatedLikedChapters[chap.chapter_id] = chap.is_liked;
      });
      setBook({ ...data, author_name: authorName, chapters: publishedChapters });
      setCurrentChapter(chapter);
      setLikedChapters(updatedLikedChapters);
      setViews(Number.isNaN(initialViews) ? 0 : initialViews + 1);
      await incrementViewCount();
      setLoading(false);
    } catch (error) {
      console.error('Error fetching book details:', error);
      setLoading(false);
    }
  };

  const incrementViewCount = async () => {
    if (!book || !currentChapter) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await api.post(`/bookReader/books/${bookId}/chapters/${currentChapter.chapter_id}/incrementViews`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBookDetails();
  }, [chapterId]);

  useEffect(() => {
    const fetchComments = async () => {
      if (!currentChapter) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        const response = await api.get(`/bookReader/chapters/${currentChapter.chapter_id}/comments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setComments(response.data);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };
    fetchComments();
  }, [currentChapter]);

  const toggleChapters = () => {
    setShowChapters(!showChapters);
  };

  const handleLike = async () => {
    if (!currentChapter) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await api.post(`/bookReader/chapters/${currentChapter.chapter_id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const isLiked = response.data.isLiked;
      const updatedLikes = isLiked 
        ? currentChapter.likes + 1
        : currentChapter.likes - 1;
      setCurrentChapter((prev) => ({
        ...prev,
        likes: updatedLikes,
      }));
      setLikedChapters((prev) => ({
        ...prev,
        [currentChapter.chapter_id]: isLiked,
      }));
    } catch (error) {
      console.error('Error liking the chapter:', error);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      await api.post(`/bookReader/books/${bookId}/chapters/${currentChapter.chapter_id}/comments`, {
        content: newComment,
        parentCommentId: replyTo,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewComment('');
      setReplyTo(null);
      const response = await api.get(`/bookReader/chapters/${currentChapter.chapter_id}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const comment = comments.find((comment) => comment.comment_id === commentId);
    if (comment.user_id !== currentUserId) {
      alert('Yalnızca yorumun sahibi olan kişi silebilir.');
      return;
    }
    try {
      await api.delete(`/bookReader/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComments((prev) => prev.filter((comment) => comment.comment_id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const toggleCommentLike = async (commentId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const response = await api.post(`/bookReader/comments/${commentId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComments((prev) => prev.map((comment) => {
        if (comment.comment_id === commentId) {
          const newLikesCount = response.data.isLiked
            ? parseInt(comment.likes_count, 10) + 1
            : Math.max(0, parseInt(comment.likes_count, 10) - 1);
          return {
            ...comment,
            is_liked_by_user: response.data.isLiked,
            likes_count: newLikesCount,
          };
        }
        return comment;
      }));
    } catch (error) {
      console.error('Error liking the comment:', error);
    }
  };

  const startReply = (commentId) => {
    setReplyTo(commentId);
  };

  const changeChapter = (newChapterId) => {
    if (newChapterId) {
      navigate(`/read/${bookId}/${newChapterId}`);
    }
  };

  const preventCopying = (event) => {
    if (event.type === 'contextmenu') {
      event.preventDefault();
    }
    if (
      (event.ctrlKey || event.metaKey) &&
      (event.key === 'c' || event.key === 'x' || event.key === 'u' || event.key === 'a')
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    document.addEventListener('contextmenu', preventCopying);
    document.addEventListener('keydown', preventCopying);
    return () => {
      document.removeEventListener('contextmenu', preventCopying);
      document.removeEventListener('keydown', preventCopying);
    };
  }, []);

  const renderComments = (comments, parentId = null) => {
    return comments
      .filter((comment) => comment.parent_comment_id === parentId)
      .map((comment) => (
        <React.Fragment key={comment.comment_id}>
          <li className="comment">
            <div className="profile-details">
              <img
                src={getProfileImageUrl(comment.profile_image_url)}
                alt="Profil"
                className="profile-picture"
              />
              <span className="comment-username">{comment.username}</span>
            </div>
            <div className="comment-content">
              <p className="comment-text">{comment.content}</p>
              <div className="reply-container">
                {parentId === null && (
                  <button
                    onClick={() => startReply(comment.comment_id)}
                    className="reply-button"
                  >
                    Yanıtla
                  </button>
                )}
              </div>
              {replyTo === comment.comment_id && parentId === null && (
                <div className="reply-input">
                  <input
                    type="text"
                    placeholder="Yanıt yaz..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addComment()}
                  />
                  <button onClick={addComment} className="send-reply-button">
                    <Send size={20} color="#fff" />
                  </button>
                </div>
              )}
              <ul className="comments-list">
                {renderComments(comments, comment.comment_id)}
              </ul>
              <div className="comment-actions">
                <button
                  className="like-button"
                  onClick={() => toggleCommentLike(comment.comment_id)}
                >
                  <Heart
                    size={16}
                    fill={comment.is_liked_by_user ? '#310034' : 'none'}
                    color={comment.is_liked_by_user ? '#310034' : '#666'}
                  />
                  <span>{comment.likes_count}</span>
                </button>
                {comment.user_id === currentUserId && (
                  <button
                    className="delete-button"
                    onClick={() => deleteComment(comment.comment_id)}
                  >
                    Sil
                  </button>
                )}
              </div>
            </div>
          </li>
        </React.Fragment>
      ));
  };

  // Düzeltilmiş convertNewlinesToBreaks fonksiyonu
  const convertNewlinesToBreaks = (text) => {
    if (!text) {
      return '';
    }
    
    // Her paragrafı ayrı bir div içine alalım ve paragraph sınıfını ekleyelim
    return text.split('\n').map((item, index) => (
      <div key={index} className="paragraph">
        {item}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="book-reader">
        <div className="loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  if (!book || !currentChapter) {
    return (
      <div className="book-reader">
        <div className="error-message">Kitap bilgileri yüklenemedi. Lütfen daha sonra tekrar deneyin.</div>
      </div>
    );
  }

  return (
    <div className="book-reader">
      <div className="header-section">
        <div className="button-strip">
          <div className="book-header" onClick={toggleChapters}>
            <div className="book-details">
              <span className="book-title">{book.title}</span>
              <span className="book-author">{book.author_name || 'Bilinmeyen Yazar'} tarafından</span>
            </div>
            <span className="dropdown-arrow">{showChapters ? '▲' : '▼'}</span>
          </div>
          <div className="buttons">
            <button className="back-to-book" onClick={() => navigate(`/book/${bookId}`)}>
              Kitaba Geri Dön
            </button>
            <button
              className={`like-button-header ${likedChapters[currentChapter.chapter_id] ? 'liked' : ''}`}
              onClick={handleLike}
            >
              <Heart
                size={20}
                fill={likedChapters[currentChapter.chapter_id] ? '#310034' : 'none'}
                color={likedChapters[currentChapter.chapter_id] ? '#310034' : '#310034'}
              />
              <span>{likedChapters[currentChapter.chapter_id] ? 'Beğenildi' : 'Beğen'}</span>
            </button>
          </div>
        </div>
        {showChapters && (
          <div className="chapter-list">
            {book.chapters.map((chapter) => (
              <div
                key={chapter.chapter_id}
                className={`chapter-item ${chapter.chapter_id === parseInt(chapterId, 10) ? 'active' : ''}`}
                onClick={() => changeChapter(chapter.chapter_id)}
              >
                <span>{chapter.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="read-main">
        <div className="story-info">
          <h1>{currentChapter.title}</h1>
          <div className="story-stats">
            <span>
              <Eye size={18} /> {views} okuma
            </span>
            <span>
              <Heart size={18} fill={likedChapters[currentChapter.chapter_id] ? '#310034' : 'none'} /> {currentChapter.likes} beğeni
            </span>
            <span>
              <MessageCircle size={18} /> {comments.length} yorum
            </span>
          </div>
        </div>
        <div className="story-content" style={{ userSelect: 'none' }}>
          {convertNewlinesToBreaks(currentChapter.content)}
        </div>
        <div className="chapter-navigation">
          <button
            onClick={() => changeChapter(book.chapters[currentChapter.chapter_number - 2]?.chapter_id)}
            disabled={currentChapter.chapter_number === 1}
          >
            <ArrowLeft size={20} /> Önceki Bölüm
          </button>
          <button
            onClick={() => changeChapter(book.chapters[currentChapter.chapter_number]?.chapter_id)}
            disabled={currentChapter.chapter_number === book.chapters.length}
          >
            Sonraki Bölüm <ArrowRight size={20} />
          </button>
        </div>
        <div className="comment-section">
          <h3>Yorumlar ({comments.length})</h3>
          <div className="comment-input">
            <input
              type="text"
              placeholder="Bir yorum yaz..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addComment()}
            />
            <button onClick={addComment} className="send-button">
              <Send size={20} color="#fff" />
            </button>
          </div>
          {comments.length > 0 ? (
            <ul className="comments-list">{renderComments(comments)}</ul>
          ) : (
            <div className="no-comments">İlk yorumu sen yap!</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookReader;